import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, Observable, of } from 'rxjs';
import { VorgangsTyp } from 'src/app/domain/model/vorgangsTyp';
import { Dokument } from 'src/app/domain/model/dokument';
import { Zuweisung } from 'src/app/domain/model/zuweisung';
import { ConfirmDialogService } from 'src/app/service/common/confirm-dialog/confirm-dialog.service';
import { environment } from '../../../environments/environment';
import { AufgabeService } from '../../service/aufgabe/aufgabe.service';
import { HttpClient } from '@angular/common/http';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { UflaUser } from '../../service/auth/ufla-user';
import { OrganisationService } from '../../service/organisation/organisation.service';
import { Organisation } from 'src/app/service/organisation/organisation';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UpAndDownloadComponent } from '../../common/upload/up-and-download-component';
import { tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ufla-zuweisung-details',
    templateUrl: './zuweisung-details.component.html',
    styleUrls: [
        './zuweisung-details.component.scss',
    ],
    standalone: false,
})
export class ZuweisungDetailsComponent extends UpAndDownloadComponent implements OnInit {
    private aufgabeService = inject(AufgabeService);
    private activatedRoute = inject(ActivatedRoute);
    authenticationService = inject(AuthenticationService);
    organisationService = inject(OrganisationService);

    protected http: HttpClient;
    private snackBar = inject(MatSnackBar);
    private confirmDialogService = inject(ConfirmDialogService);

    public loadError: boolean = false;
    private zuweisungId: string = '';
    public zuweisung$: Observable<Zuweisung>;
    public dokumente$: Observable<Dokument[]>;
    public replies$: Observable<Dokument[]>;
    public columnsForDocuments = [
        'fileName',
        'lastModified',
        'icon',
    ];
    public filesControl = new FormControl();
    public uflaUser: UflaUser;
    public organisation: Organisation;
    public emailSubject = 'TNB-Portal UFLA: Kontaktaufnahme ';
    public emailBody = 'Kontaktaufnahme zu Vorgang {0} vom Typ {1} aus dem Jahr {2} %0D%0A%0D%0ASehr geehrte Damen und Herren, %0D%0A%0D%0A';
    public ccEmailAdressen;

    public fileValidatorOptions = { maxFileSizeBytes: 150 * 1024 * 1024 };

    constructor() {
        super();

        this.authenticationService.user$
            .pipe(takeUntilDestroyed())
            .subscribe((user) => {
                this.uflaUser = user;
                this.organisationService.getOrganisation(user.organisationsId)
                    .pipe(takeUntilDestroyed())
                    .subscribe(org => {
                        this.organisation = org;
                        this.emailSubject += this.organisation.name;
                        this.ccEmailAdressen = this.organisation.emailAdressen;
                    });
            });
    }

    ngOnInit() {
        this.activatedRoute.paramMap.subscribe((map) => {
            this.zuweisungId = String(map.get('id'));
            this.loadZuweisung(this.zuweisungId);
        });
    }

    /*
   * Functional change: At development start every assigned task forced
   * uploading a reply document. Since #179862070 a reply document now is only
   * mandatory when the original vorgang was created with an attachment.
   */
    public requiresReply(zuweisung: Zuweisung): boolean {
        return (zuweisung.aufgabe.type === VorgangsTyp.AUFGABE || zuweisung.aufgabe.type === VorgangsTyp.ABWURFPUNKTE_AUFGABE);
    }

    public isFileSizeError(): boolean {
        return this.filesControl.hasError('maxFileSize');
    }

    public isFilePresent(): boolean {
        return !this.filesControl.invalid && this.filesControl.value?.[0];
    }

    public canBeResolved(zuweisung: Zuweisung): boolean {
        return (zuweisung.aufgabe.type === VorgangsTyp.INFORMATION
        || zuweisung.aufgabe.type === VorgangsTyp.TERMIN || zuweisung.aufgabe.type === VorgangsTyp.ABWURFPUNKTE_INFORMATION
        || zuweisung.aufgabe.type === VorgangsTyp.GESAMTLAST || zuweisung.dokumentVorhanden
        || (zuweisung.aufgabe.type === VorgangsTyp.AUFGABE &&
              !zuweisung.aufgabe.hatDokument) || (zuweisung.aufgabe.type === VorgangsTyp.ABWURFPUNKTE_AUFGABE && !zuweisung.aufgabe.hatDokument))
      && !this.authenticationService.isVnbObserver(this.uflaUser);
    }

    private loadZuweisung(zuweisungId: string) {
        try {
            this.zuweisung$ = this.aufgabeService.getZuweisung(zuweisungId);
            this.zuweisung$.pipe(
                tap((zuweisung) => {
                    this.dokumente$ = this.aufgabeService.listCreaterDocumentAsReceiver(zuweisung.aufgabe.id.toString());
                    this.replies$ = this.aufgabeService.listReplies(zuweisung.id.toString());
                    this.emailBody = this.emailBody
                        .replace('{0}', zuweisung.aufgabe.name)
                        .replace('{1}', zuweisung.aufgabe.type);

                    if (zuweisung.aufgabe.frist) {
                        const fristDate = new Date(zuweisung.aufgabe.frist);
                        this.emailBody = this.emailBody.replace('{2}', fristDate.getFullYear().toString());
                    }
                }), catchError(() => {
                    this.loadError = true;
                    return of(null);
                }),
            ).subscribe();
        } catch (error) {
            this.loadError = true;
            error;
        }

    }

    public downloadDocument(aufgabeId: string, dokument: Dokument) {
        let downloadTargetUrl = '/attachment/aufgabe/' + aufgabeId + '/dokument/' + dokument.fileName;
        const fileDownloadUrl = environment.API_URL + downloadTargetUrl;
        this.createAndClickDownloadLink(dokument, fileDownloadUrl);
    }

    public async markDone() {
        if (await this.confirmDialogService.showDialog(
            'Abgeschlossene Vorgänge können nicht wieder geöffnet werden. Sind Sie sicher, ' +
      'dass Sie diesen Vorgang abschließen möchten?', 'Vorgang abschließen',
            'primary', 'Ja', 'Nein')) {
            const ok = await this.aufgabeService.markZuweisungDone(this.zuweisungId);
            if (ok) {
                this.snackBar.open('Vorgang erledigt', 'Ok', {
                    duration: 2000,
                });
                this.loadZuweisung(this.zuweisungId);
            } else {
                await this.confirmDialogService.showDialog(
                    'Abschließen des Vorgangs konnte nicht durchgeführt werden, bitte später erneut versuchen oder den Support kontaktieren.',
                    'Fehler',
                    'primary',
                    'Ok',
                    null,
                );
                this.resetUploadState();
            }
        }
    }

    public async storeReply(zuweisung: Zuweisung) {
        if (this.filesControl.value[0]) {
            try {
                await this.aufgabeService.storeReply(zuweisung, this.filesControl.value[0], this);
                this.snackBar.open('Datei wurde hochgeladen.', 'Ok', {
                    duration: 5000,
                });
                this.resetUploadState();
            } catch (error) {
                await this.confirmDialogService.showDialog(
                    'Hochladen des Dokuments leider fehlgeschlagen, bitte später erneut versuchen oder den Support kontaktieren.',
                    'Fehler',
                    'primary',
                    'Ok',
                    null,
                );
                this.resetUploadState();
                error;
            }
        }
    }

    public resetUploadState() {
        this.filesControl.setValue(null);
        this.uploading = false;
        this.loadZuweisung(this.zuweisungId);
    }

    public downloadReply(zuweisungId: number, dokument: Dokument) {
        const fileDownloadUrl = environment.API_URL + '/attachment/zuweisung/' + zuweisungId + '/dokument/' + dokument.fileName;
        this.createAndClickDownloadLink(dokument, fileDownloadUrl);
    }

    public async removeReply(fileName: string) {
        await this.aufgabeService.removeReply(this.zuweisungId, fileName);
        this.snackBar.open('Datei "' + fileName + '" wurde entfernt.', 'Ok', {
            duration: 5000,
        });
        this.loadZuweisung(this.zuweisungId);
    }

}
